import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import {
  formatCompanionDate,
  formatCompanionMoney,
} from '@/components/companion/feed/companion-formatters';
import { Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionRightsExpiringEvidence } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The display rights renewal card, as the companion panel shows it.
 *
 * Every line comes from the evidence the server froze: the day the rights stop
 * covering launches, the conditions the agreement was struck on, and how the
 * content did against the brand's own recent ones — but that last line only
 * when there were enough of them to compare. A brand about to commit money for
 * another term is told plainly when there is nothing to compare against, rather
 * than shown a figure standing on two contents.
 *
 * The call to action prepares a request. It never says the rights were
 * extended: the creator has not answered, and only their answer can do that.
 */

interface RightsExpiringCardProps {
  action: CompanionActionDto & { evidence: CompanionRightsExpiringEvidence };
  /** Opens the composer on this authorization. Wired by the panel, never here. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

export const RightsExpiringCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: RightsExpiringCardProps) => {
  const { t, i18n } = useTranslation();
  const { evidence } = action;
  const language = i18n.language;

  const creatorName =
    evidence.creatorDisplayName ?? t('companion.cards.rightsExpiring.unknownCreator');
  const amount = formatCompanionMoney(
    { amountCents: evidence.terms.amountCents, currencyCode: evidence.terms.currencyCode },
    language
  );
  const termsKey =
    evidence.terms.compensationModel === 'flat'
      ? 'companion.cards.rightsExpiring.termsFlat'
      : 'companion.cards.rightsExpiring.terms';

  return (
    <CompanionCardShell
      testId="companion-rights-expiring-card"
      dataAttrs={{ 'data-authorization-id': evidence.authorizationId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader
        title={action.title}
        reason={action.reason}
        eyebrow={
          /* The deadline is the whole reason the card exists, so it is stated
             where the eye lands first and in the one colour the dashboard
             reserves for something running out. */
          <Text
            fontFamily="heading"
            fontSize="10.5px"
            fontWeight="700"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="warning.text"
            data-testid="rights-expiring-deadline"
          >
            {t('companion.cards.rightsExpiring.deadline', { count: evidence.daysRemaining })}
          </Text>
        }
      >
        <Text fontSize="13px" color="ink" lineHeight="1.45">
          {creatorName}
        </Text>
      </CompanionCardHeader>

      <Stack gap="2px">
        <EvidenceLine>
          {t('companion.cards.rightsExpiring.endsAt', {
            date: formatCompanionDate(evidence.endsAt, language),
          })}
        </EvidenceLine>
        <EvidenceLine>{t(termsKey, { amount, count: evidence.terms.durationMonths })}</EvidenceLine>
        {evidence.attestedOffPlatform ? (
          <EvidenceLine>{t('companion.cards.rightsExpiring.attested')}</EvidenceLine>
        ) : null}
        <EvidenceLine>
          {evidence.performance
            ? t('companion.cards.rightsExpiring.performance', {
                value: evidence.performance.value.toFixed(1),
                median: evidence.performance.medianOver90d.toFixed(1),
                count: evidence.performance.comparableCount,
              })
            : t('companion.cards.rightsExpiring.performanceUnavailable')}
        </EvidenceLine>
      </Stack>
    </CompanionCardShell>
  );
};
